/**
 * Leitura das regras de obrigatoriedade do cadastro de paciente para o e-SUS APS.
 *
 * A regra é **lida**, nunca reimplementada: `GET /empresas/{empresaId}/esus/cadastro-paciente`
 * devolve `{esusAtivo, camposObrigatorios: [{campo, correcao, condicao}]}` percorrendo as mesmas
 * constantes de `CampoObrigatorioFci` que o backend cobra na escrita. Se a tabela do LEDI mudar, o
 * frontend não muda.
 *
 * O que **mora aqui** é só a tradução da `condicao` publicada para o estado do formulário — e são
 * três campos observados, todos conferidos no enum do backend:
 *  - `dadosAdicionais.nacionalidade` → compara o **código LEDI** (1, 2, 3);
 *  - `dadosAdicionais.raca` → compara o `name` da constante (`'INDIGENA'`);
 *  - `pessoa.cpfOuCns` com o marcador `'SEM_CPF'` → verdadeiro quando o cadastro está sem CPF.
 *
 * Campo observado desconhecido não inventa comportamento: a regra é tratada como **não aplicável**
 * (fail-open no cliente, com `console.warn`). A barreira real é o 422 do backend; o gate da tela é
 * defesa em profundidade.
 */

import axios, { AxiosResponse } from 'axios'
import { cachedRequest } from '../../../../../../_metronic/helpers/requestCache'

const API_URL = process.env.REACT_APP_API_URL

/** Condição que liga uma regra. Nula quando a regra é incondicional. */
export type CondicaoCampoFci = {
  campo: string
  valores: Array<string | number>
}

export type CampoObrigatorioFci = {
  campo: string
  correcao: string
  condicao?: CondicaoCampoFci | null
}

export type RegrasCadastroPacienteFci = {
  esusAtivo: boolean
  camposObrigatorios: CampoObrigatorioFci[]
}

/**
 * Resposta usada quando a empresa não foi consultada ainda, quando ela não usa e-SUS, e também
 * quando o endpoint falha (AC8: a tela degrada com a obrigatoriedade de hoje, sem travar o submit).
 */
export const REGRAS_FCI_INATIVAS: RegrasCadastroPacienteFci = {
  esusAtivo: false,
  camposObrigatorios: [],
}

/**
 * Busca as regras da empresa.
 *
 * `cachedRequest` com o TTL padrão de 60s do repositório — mesmo horizonte do cache Redis do backend
 * (Dev Evidence da F3): quem acabou de ligar a integração vê a mudança dentro de um minuto, e abrir
 * o modal cinco vezes seguidas não vira cinco requisições. Chave por empresa, para que dois tenants
 * nunca compartilhem a resposta.
 */
export const getRegrasCadastroPacienteFci = (
  empresaId: number
): Promise<RegrasCadastroPacienteFci> =>
  cachedRequest(`esus/cadastro-paciente/${empresaId}`, () =>
    axios
      .get(`${API_URL}/empresas/${empresaId}/esus/cadastro-paciente`)
      .then((resp: AxiosResponse<RegrasCadastroPacienteFci>) => resp.data)
  )

/**
 * Estado do formulário que as condições observam.
 *
 * `codigoLedi` é o código do LEDI da nacionalidade escolhida (1/2/3), **não** o id da linha do
 * catálogo que o formik guarda em `values.nacionalidade.value`. Confundir os dois faz a tela exigir
 * data de naturalização de quem é brasileiro — é a armadilha nomeada no Risk 3 do plano, e por isso
 * a conversão id → código acontece fora daqui (no hook, a partir do `onOptionsLoaded` do combo) e
 * chega pronta neste contexto.
 */
export type ContextoCondicaoFci = {
  codigoLediNacionalidade?: number | null
  raca?: string | null
  semCpf?: boolean
}

/** Marcador publicado pelo backend em `Condicao.SEM_CPF`. */
const MARCADOR_SEM_CPF = 'SEM_CPF'

const contem = (valores: Array<string | number>, valor: string | number | null | undefined) =>
  valor !== null && valor !== undefined && valores.some((item) => String(item) === String(valor))

/**
 * A regra vale para o estado atual do formulário?
 *
 * Regra sem `condicao` é incondicional e responde sempre `true`.
 */
export const condicaoAtendida = (
  condicao: CondicaoCampoFci | null | undefined,
  contexto: ContextoCondicaoFci
): boolean => {
  if (!condicao) return true

  const valores = condicao.valores || []

  switch (condicao.campo) {
    case 'dadosAdicionais.nacionalidade':
      return contem(valores, contexto.codigoLediNacionalidade)
    case 'dadosAdicionais.raca':
      return contem(valores, contexto.raca)
    case 'pessoa.cpfOuCns':
      // O único valor previsto é o marcador de estado (não é valor de campo).
      return valores.includes(MARCADOR_SEM_CPF) && contexto.semCpf === true
    default:
      // Fail-open declarado: campo observado que esta versão da tela não conhece não pode inventar
      // obrigatoriedade nem escondê-la em silêncio.
      console.warn(
        `[e-SUS] Condição sobre campo desconhecido "${condicao.campo}": regra ignorada no cliente. ` +
          'O 422 do backend continua sendo a barreira.'
      )
      return false
  }
}

/** Os campos exigidos **agora**, já filtrados pelas condições. */
export const camposExigidosAgora = (
  regras: RegrasCadastroPacienteFci | undefined,
  contexto: ContextoCondicaoFci
): CampoObrigatorioFci[] => {
  if (!regras?.esusAtivo) return []
  return (regras.camposObrigatorios || []).filter((campo) =>
    condicaoAtendida(campo.condicao, contexto)
  )
}
